/**
 * ฉากของโซน — พื้น ทางเดิน ลานเมือง ตะเกียง ต้นไม้ หิน ดอกไม้ และบ่อน้ำ
 *
 * ย้ายมาจาก `demo/wickhaven.html` ส่วน world แล้วเปลี่ยนเป็น import แยกโมดูล
 *
 * **ทุกอย่างในไฟล์นี้เป็นของตกแต่งล้วน ไม่มีการชนและไม่มีผลต่อเกม**
 * ถ้าจะทำให้ต้นไม้กีดขวางจริง ต้องให้ server รู้ด้วย ไม่งั้น client จะเดินคนละทางกับ server
 * (เดโมมีการชนฝั่ง client ได้เพราะเล่นคนเดียว ไม่มีเซิร์ฟเวอร์มาเถียง)
 *
 * ตำแหน่งของทุกชิ้นสุ่มจาก seed คงที่ ทุกคนจึงเห็นฉากเดียวกัน
 * ถ้าใช้ Math.random() แต่ละเครื่องจะเห็นต้นไม้คนละที่ แล้วนัดเจอกัน "ใต้ต้นไม้" ไม่ได้
 */

import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import type { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import type { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Color3, Color4, Vector3 } from "@babylonjs/core/Maths/math";
import { startAmbient } from "./ambient.js";
import { groundMaterial, scatterGround, type GraphicsQuality } from "./ground-detail.js";
import { buildProps, shadeHex } from "./props.js";
import { hasLantern, lanternPos, distToRoute, routeNetwork, wallDistance, zoneHalf, PORTAL_RADIUS, type PortalDef, type ZoneDef } from "@wickhaven/shared";

/** ชื่อของ mesh ก้อนเดียวที่รวมของตกแต่งทั้งหมดไว้ */
export const DECO_MESH_NAME = "deco";
/** ขอบเขตที่วางของตกแต่ง (หน่วยโลก จากจุดกลาง) */
const DECO_REACH = 46;
/** รัศมีลานเมืองกลางแผนที่ */
const PLAZA_R = 6.5;
/** ครึ่งความกว้างของทางเดิน เผื่อให้ของไม่ไปขวางทาง */
const PATH_HALF = 2.6;

interface MatOpts {
  em?: string;
  alpha?: number;
  noLight?: boolean;
}

/** แคชแยกตามฉาก ด้วยเหตุผลเดียวกับใน models.ts (วัสดุข้ามฉากจะไม่วาดอะไรเลย) */
const matCache = new WeakMap<Scene, Map<string, StandardMaterial>>();

function mat(scene: Scene, hex: string, opts: MatOpts = {}): StandardMaterial {
  let perScene = matCache.get(scene);
  if (!perScene) {
    perScene = new Map();
    matCache.set(scene, perScene);
  }

  const key = hex + JSON.stringify(opts);
  const found = perScene.get(key);
  if (found) return found;

  const m = new StandardMaterial(`w${key}`, scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.specularColor = Color3.Black();
  if (opts.em) m.emissiveColor = Color3.FromHexString(opts.em);
  if (opts.alpha != null) m.alpha = opts.alpha;
  if (opts.noLight) m.disableLighting = true;
  m.freeze();
  perScene.set(key, m);
  return m;
}

function flat(mesh: Mesh): Mesh {
  mesh.convertToFlatShadedMesh();
  return mesh;
}

/**
 * ตัวสุ่มที่ให้ผลเหมือนกันทุกเครื่อง (Lehmer LCG ตัวเดียวกับในเดโม)
 * ไม่ใช้ Math.random เพราะฉากต้องตรงกันทุกคน
 */
function makeRandom(seed: number): () => number {
  let s = seed;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

export interface World {
  /** พื้นที่คลิกเพื่อเดิน main.ts เทียบด้วย === กับ mesh ที่คลิกโดน */
  ground: Mesh;
  /**
   * วางของตกแต่งตามข้อมูลโซน เรียกตอนรู้โซนจาก server
   * เรียกซ้ำไม่มีผล (กันการเข้าห้องใหม่แล้วฉากซ้อนกันสองชั้น)
   */
  decorate(zone: ZoneDef | undefined): void;
  /** ตะเกียงจุดวาร์ปของโซนนี้ติดไฟหรือยัง (ดับ = ยังไม่ได้จุด) เรียกซ้ำได้ ไม่มีตะเกียงก็ไม่ทำอะไร */
  setLanternLit(lit: boolean): void;
}

/** คุณภาพกราฟิก (main.ts ตั้งจาก settings.graphics ก่อน decorate) — low = ของเล็กบนพื้นน้อยลง */
let quality: GraphicsQuality = "high";
export function setWorldQuality(q: GraphicsQuality): void {
  quality = q;
}

/** สีพื้นเมื่อโซนไม่ได้กำหนด `ground` เอง */
const DEFAULT_GROUND = "#6b9e5c";

export function buildWorld(scene: Scene): World {
  const ground = MeshBuilder.CreateGround("ground", { width: 120, height: 120 }, scene);
  ground.material = mat(scene, DEFAULT_GROUND);
  let currentZoneKey = "";
  let worldNodes: (Mesh | TransformNode)[] = [];
  let stopAmbient: (() => void) | null = null;
  let stopAnim: (() => void) | null = null;
  let way: LanternParts | null = null;
  let wayLit: boolean | null = null;
  let worldLights: PointLight[] = [];
  scene.onBeforeRenderObservable.add(() => followFog(scene));

  function clearWorldDecorations(): void {
    stopAmbient?.();
    stopAmbient = null;
    stopAnim?.();
    stopAnim = null;
    // ไฟไม่อยู่ใน scene.meshes ต้องเก็บแยก ไม่งั้นสลับโซนแล้วไฟตะเกียงเก่าค้าง
    for (const l of worldLights) l.dispose();
    worldLights = [];
    way = null;
    wayLit = null;
    for (const node of worldNodes) {
      try {
        node.dispose();
      } catch {}
    }
    worldNodes = [];
  }

  return {
    ground,
    setLanternLit(lit) {
      if (!way || wayLit === lit) return;
      wayLit = lit;
      way.flame.setEnabled(lit);
      way.lamp.setEnabled(lit);
      // ดับ = กรงหม่นไม่เรืองแสง · ติด = สีเดิมเรืองแสง (วัสดุแคชร่วม ห้ามแก้ค่าในวัสดุ สลับวัสดุแทน)
      way.cage.material = lit ? mat(scene, "#ffd27a", { em: "#ffb547" }) : mat(scene, "#5a5248");
    },
    decorate(zone) {
      const zoneKey = zone?.id ?? "__none__";
      if (currentZoneKey === zoneKey) return;
      clearWorldDecorations();
      currentZoneKey = zoneKey;

      const beforeMeshes = new Set(scene.meshes);
      const beforeLights = new Set(scene.lights);
      const beforeNodes = new Set(scene.transformNodes);

      // พื้นกว้างตามขนาดโซน (เผื่อขอบให้ภูเขารอบนอกตั้งบนพื้น) · มีลายตามธีม (ground-detail.ts)
      const groundSpan = (zoneHalf(zone) + 20) * 2;
      ground.material = groundMaterial(scene, themeOf(zone), zone?.ground ?? DEFAULT_GROUND, groundSpan);
      ground.scaling.x = groundSpan / 120;
      ground.scaling.z = groundSpan / 120;
      applyAtmosphere(scene, themeOf(zone), zone?.ground);
      // ตะเกียง โรงตีเหล็ก ลาน และแผงร้าน มีเฉพาะโซนที่เป็นเมือง
      // **สร้างตอนรู้โซนแล้วเท่านั้น** เดิมสร้างตอนเริ่มฉากเสมอ ทุกโซนจึงมีตะเกียงกลางแผนที่
      // ไม่รู้โซน (เทสต์ หรือข้อมูลเก่า) ถือเป็นเมือง ให้หน้าตาเหมือนก่อนมีหลายโซน
      if (isTown(zone)) {
        way = buildLantern(scene, 0, 0, "lantern", 1);
        buildForge(scene);
        // ในเมืองอื่นที่ไม่ใช่เมืองหลักแรก ตะเกียงสามารถคลิกจุดไฟเพื่อปลดล็อกจุดวาร์ปได้
        if (zone && zone.id !== "meadow") {
          const pick = MeshBuilder.CreateCylinder("lantern-pick", { diameter: 2.8, height: 6 }, scene);
          pick.position.set(0, 3, 0);
          pick.visibility = 0;
          pick.isPickable = true;
          pick.metadata = { kind: "lantern" };
        }
      }
      // ตะเกียงจุดวาร์ปข้างจุดเกิด (shared/waypoints.ts) เมืองใช้ตะเกียงวิญญาณกลางลานแทน
      if (zone && !isTown(zone) && hasLantern(zone)) {
        const l = lanternPos(zone);
        way = buildLantern(scene, l.x, l.z, "waylantern", 0.8);
        // ตัวรับคลิก (ล่องหน · ห้ามใช้ isVisible=false ไม่งั้น pick ข้าม) — คลิกตะเกียงเพื่อจุดไฟ
        const pick = MeshBuilder.CreateCylinder("lantern-pick", { diameter: 2.2, height: 5 }, scene);
        pick.position.set(l.x, 2.5, l.z);
        pick.visibility = 0;
        pick.isPickable = true;
        pick.metadata = { kind: "lantern" };
      }
      for (const portal of zone?.portals ?? []) buildPortal(scene, portal);
      buildDecoration(scene, zone);

      stopAnim = animateZone(scene);
      // อนุภาคล้างตัวเองผ่าน stopAmbient ไม่ต้องจำใน worldNodes (สร้างหลังจดรายการ)
      // จำ mesh และ node ที่สร้างขึ้นใหม่ เพื่อนำไป dispose ตอนสลับโซน
      for (const m of scene.meshes) {
        if (!beforeMeshes.has(m) && m !== ground) worldNodes.push(m);
      }
      for (const n of scene.transformNodes) {
        if (!beforeNodes.has(n)) worldNodes.push(n);
      }
      for (const l of scene.lights) {
        if (!beforeLights.has(l) && l instanceof PointLight) worldLights.push(l);
      }
      stopAmbient = startAmbient(scene, themeOf(zone));
    },
  };
}

/**
 * ท่าทางของของที่ไม่ได้รวมก้อน: เปลวตะเกียงกะพริบ วงประตูหายใจ ถ่านเตาวูบวาบ
 * หาจากชื่อ mesh ที่ world สร้างเอง คืนฟังก์ชันหยุด
 */
function animateZone(scene: Scene): () => void {
  const flame = scene.getMeshByName("flame");
  const coals = scene.getMeshByName("coals");
  const lamp = scene.getLightByName("lamp");
  const rings = scene.meshes.filter((m) => m.name === "pring" || m.name === "pglow");
  let t = 0;
  const obs = scene.onBeforeRenderObservable.add(() => {
    t += Math.min(0.1, scene.getEngine().getDeltaTime() / 1000);
    const flick = 1 + Math.sin(t * 9) * 0.06 + Math.sin(t * 23) * 0.04;
    flame?.scaling.setAll(flick);
    if (lamp) lamp.intensity = 0.9 * flick;
    if (coals) coals.scaling.y = 1 + Math.sin(t * 5) * 0.12;
    for (const r of rings) r.visibility = 0.75 + Math.sin(t * 2.2) * 0.25;
  });
  return () => { scene.onBeforeRenderObservable.remove(obs); };
}

/** ชุดหน้าตาของโซน: `theme` ใน zones.json ถ้ามี ไม่งั้น id โซน */
export function themeOf(zone: ZoneDef | undefined): string {
  return zone?.theme ?? zone?.id ?? "meadow";
}

/** สีกรวยต้นไม้ไกล ๆ ตามธีม (ไม่มีในรายการ = เขียวป่า) */
const FAR_TREE: Record<string, string> = {
  forest: "#2d5a27", jungle: "#2f7a3a", snow: "#2d4a3e", dungeon_frost: "#3d5a6e", desert: "#386641",
  volcano: "#211815", crystal_cave: "#3a8a9a", graveyard: "#2e2a28", celestial: "#e8c45a", swamp: "#4a5338",
  coast: "#2f855a", storm: "#2f4a40", ruins: "#8a8678", abyss_ruins: "#1a102f", root_cave: "#4a3b2c", dungeon_mine: "#3e342b",
};

function isTown(zone: ZoneDef | undefined): boolean {
  return zone ? zone.town === true : true;
}

/**
 * seed ของการสุ่มของตกแต่ง ต่อโซน
 *
 * **ทุ่งดอกเช้า (และไม่รู้โซน) ใช้ 7 เหมือนเดิม** ผังต้นไม้ที่ผู้เล่นคุ้นอยู่แล้วจะไม่เปลี่ยน
 * โซนอื่นคิดจาก id จึงคงที่ทุกเครื่อง (กติกาเดิม: ห้ามใช้ Math.random) แต่ไม่ซ้ำกับเมือง
 */
function zoneSeed(zone: ZoneDef | undefined): number {
  if (!zone || zone.id === "meadow") return 7;
  let h = 0;
  for (const ch of zone.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (h % 9973) + 11;
}

/**
 * ประตูเชื่อมโซน — เสาหินสองต้น คานบน และวงแสงบนพื้น
 *
 * วงแสงกว้างเท่า PORTAL_RADIUS ที่ server ใช้ตัดสินจริง ผู้เล่นจึงเห็นว่าต้องเดินเข้าตรงไหน
 * ไม่ได้รวมเข้าก้อน deco เพราะวงแสงใช้วัสดุเรืองแสงคนละแบบ และมีแค่ไม่กี่บาน
 */
function buildPortal(scene: Scene, portal: PortalDef): void {
  const root = new TransformNode(`portal-${portal.id}`, scene);
  root.position.set(portal.x, 0, portal.z);
  // หันคานขวางทางเดินเข้า: ประตูบนขอบซ้าย-ขวาหันตาม z ขอบบน-ล่างหันตาม x
  root.rotation.y = Math.abs(portal.x) >= Math.abs(portal.z) ? Math.PI / 2 : 0;

  const ring = MeshBuilder.CreateDisc("pring", { radius: PORTAL_RADIUS, tessellation: 24 }, scene);
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.05;
  ring.material = mat(scene, "#8fd8ff", { em: "#5fb8ff", alpha: 0.38, noLight: true });
  ring.parent = root;

  for (const side of [-1, 1]) {
    const post = flat(MeshBuilder.CreateBox("ppost", { width: 0.8, height: 4.2, depth: 0.8 }, scene));
    post.position.set(side * (PORTAL_RADIUS - 0.2), 2.1, 0);
    post.material = mat(scene, "#9a958a");
    post.parent = root;
  }
  const beam = flat(MeshBuilder.CreateBox("pbeam", {
    width: PORTAL_RADIUS * 2 + 0.8, height: 0.6, depth: 1,
  }, scene));
  beam.position.y = 4.4;
  beam.material = mat(scene, "#7d786e");
  beam.parent = root;

  // วาดสองหน้า (sideOrientation 2 = DOUBLESIDE) เพราะเดินเข้าได้จากทั้งสองฝั่ง
  // ตั้งที่ตัว mesh ไม่ใช่ที่วัสดุ เพราะวัสดุถูก freeze และแคชใช้ร่วมกันทั้งฉาก
  const glow = MeshBuilder.CreatePlane("pglow", {
    width: PORTAL_RADIUS * 2 - 1.2, height: 3.8, sideOrientation: 2,
  }, scene);
  glow.position.y = 2;
  glow.material = mat(scene, "#bfe8ff", { em: "#8fd0ff", alpha: 0.28, noLight: true });
  glow.parent = root;

  for (const m of root.getChildMeshes()) m.isPickable = false;
}

/** ตะเกียงวิญญาณกลางเมือง หมุดอ้างอิงทิศของทั้งโซน */
interface LanternParts {
  cage: Mesh;
  flame: Mesh;
  lamp: PointLight;
}

function buildLantern(scene: Scene, x = 0, z = 0, name = "lantern", size = 1): LanternParts {
  const lantern = new TransformNode(name, scene);
  lantern.position.set(x, 0, z);
  lantern.scaling.setAll(size);

  const pole = flat(MeshBuilder.CreateCylinder("pole", { diameter: 0.35, height: 4.2, tessellation: 6 }, scene));
  pole.position.y = 2.1;
  pole.material = mat(scene, "#3d3427");
  pole.parent = lantern;

  const base = flat(MeshBuilder.CreateCylinder(
    "base",
    { diameterTop: 1, diameterBottom: 1.5, height: 0.6, tessellation: 6 },
    scene,
  ));
  base.position.y = 0.3;
  base.material = mat(scene, "#5b5040");
  base.parent = lantern;

  const cage = flat(MeshBuilder.CreateCylinder(
    "cage",
    { diameterTop: 0.9, diameterBottom: 1.1, height: 1.1, tessellation: 6 },
    scene,
  ));
  cage.position.y = 4.6;
  cage.material = mat(scene, "#ffd27a", { em: "#ffb547" });
  cage.parent = lantern;

  const cap = flat(MeshBuilder.CreateCylinder(
    "cap",
    { diameterTop: 0, diameterBottom: 1.5, height: 0.7, tessellation: 6 },
    scene,
  ));
  cap.position.y = 5.5;
  cap.material = mat(scene, "#3d3427");
  cap.parent = lantern;

  const flame = MeshBuilder.CreateSphere("flame", { diameter: 0.55, segments: 6 }, scene);
  flame.position.y = 4.6;
  flame.material = mat(scene, "#fff1c2", { em: "#ffe08a", noLight: true });
  flame.parent = lantern;

  const lamp = new PointLight(name === "lantern" ? "lamp" : `${name}-lamp`, new Vector3(x, 4.6 * size, z), scene);
  lamp.diffuse = Color3.FromHexString("#ffb547");
  lamp.specular = Color3.Black();
  lamp.intensity = 0.9;
  lamp.range = 14;

  for (const m of lantern.getChildMeshes()) m.isPickable = false;
  return { cage, flame, lamp };
}

/** เตาตีเหล็กและทั่งตีเหล็กในลานเมือง */
function buildForge(scene: Scene): void {
  const forge = new TransformNode("forge", scene);
  forge.position.set(-6, 0, 5);

  // ฐานเตาหิน
  const furnace = flat(MeshBuilder.CreateBox("furnace", { width: 1.8, height: 1.2, depth: 1.5 }, scene));
  furnace.position.y = 0.6;
  furnace.material = mat(scene, "#4a4540");
  furnace.parent = forge;

  // ปล่องไฟเตา
  const chimney = flat(MeshBuilder.CreateCylinder("chimney", { diameter: 0.6, height: 1.6, tessellation: 6 }, scene));
  chimney.position.set(0.3, 1.8, 0);
  chimney.material = mat(scene, "#36322e");
  chimney.parent = forge;

  // ถ่านไฟเรืองแสงในเตา
  const coals = MeshBuilder.CreateBox("coals", { width: 0.9, height: 0.4, depth: 0.5 }, scene);
  coals.position.set(-0.3, 0.5, 0.55);
  coals.material = mat(scene, "#ff4500", { em: "#ff7700", noLight: true });
  coals.parent = forge;

  // ทั่งตีเหล็กข้างเตา
  const stump = flat(MeshBuilder.CreateCylinder("stump", { diameter: 0.8, height: 0.7, tessellation: 7 }, scene));
  stump.position.set(1.6, 0.35, 0.4);
  stump.material = mat(scene, "#5c4033");
  stump.parent = forge;

  const anvil = flat(MeshBuilder.CreateBox("anvil", { width: 0.6, height: 0.35, depth: 0.3 }, scene));
  anvil.position.set(1.6, 0.85, 0.4);
  anvil.material = mat(scene, "#2f3542");
  anvil.parent = forge;

  for (const m of forge.getChildMeshes()) m.isPickable = false;
}

/** อยู่บนทางเดินหรือเปล่า ของตกแต่งต้องไม่ไปขวางทาง */
function onPath(x: number, z: number): boolean {
  if (Math.abs(x) < PATH_HALF || Math.abs(z) < PATH_HALF) return true;
  // ทางเดินสาขาไปลานฝึกซ้อมหุ่นฟาง และตัวลานประลอง
  if (Math.abs(x - -22) < 2.0 && z >= -17.5 && z <= 0) return true;
  if (Math.abs(z - -16) < 2.0 && x >= -34 && x <= -20) return true;
  if (Math.hypot(x - -32, z - -16) < 8.0) return true;
  return false;
}

function buildDecoration(scene: Scene, zone: ZoneDef | undefined): void {
  const rand = makeRandom(zoneSeed(zone));
  const town = isTown(zone);
  /** ชิ้นส่วนที่จะรวมเป็นก้อนเดียวตอนท้าย */
  const deco: Mesh[] = [];

  const add = (m: Mesh): void => {
    m.isPickable = false;
    deco.push(m);
  };

  // ---- หย่อมหญ้าสีต่างกัน ให้พื้นไม่เรียบเป็นสีเดียว ----
  const H = zoneHalf(zone);
  const S = H / DECO_REACH;
  // รอบ 20: หย่อมสีย้ายไปอยู่ใน texture พื้น (ground-detail.ts) — แผ่นกลมแบนดูเป็นสติกเกอร์บนพื้นลาย
  // ยังเรียก rand() เท่าเดิม ผังต้นไม้/หินที่สุ่มต่อจากนี้จะได้อยู่ที่เดิม
  for (let i = 0; i < Math.round(30 * S * S); i++) {
    rand(); rand(); rand(); rand();
  }

  // ---- ทางเดินสี่แฉกออกจากลานเมือง (เฉพาะเมือง) ----
  for (const [x, z, w, d] of (town ? [
    [0, -22, 3.2, 36], [0, 22, 3.2, 36], [-22, 0, 36, 3.2], [22, 0, 36, 3.2],
  ] : []) as Array<[number, number, number, number]>) {
    const b = MeshBuilder.CreateBox("path", { width: w, height: 0.04, depth: d }, scene);
    b.position.set(x, 0.02, z);
    b.material = mat(scene, "#d8c08c");
    add(b);
  }

  // ---- ทางเดินเชื่อมและลานฝึกซ้อมหุ่นฟาง (เฉพาะเมือง) ----
  if (town) {
    const branch1 = MeshBuilder.CreateBox("path_b1", { width: 2.8, height: 0.04, depth: 16 }, scene);
    branch1.position.set(-22, 0.02, -8);
    branch1.material = mat(scene, "#d8c08c");
    add(branch1);

    const branch2 = MeshBuilder.CreateBox("path_b2", { width: 12, height: 0.04, depth: 2.8 }, scene);
    branch2.position.set(-28, 0.02, -16);
    branch2.material = mat(scene, "#d8c08c");
    add(branch2);

    const trainArena = MeshBuilder.CreateCylinder("train_arena", { diameter: 15, height: 0.06, tessellation: 12 }, scene);
    trainArena.position.set(-32, 0.03, -16);
    trainArena.material = mat(scene, "#c8b382");
    add(trainArena);

    // รั้วไม้ล้อมลานซ้อม
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      if (Math.abs(Math.sin(a)) < 0.25 && Math.cos(a) > 0.5) continue; // ทางเข้า
      const post = flat(MeshBuilder.CreateBox("fpost", { width: 0.25, height: 0.95, depth: 0.25 }, scene));
      post.position.set(-32 + Math.cos(a) * 7.2, 0.47, -16 + Math.sin(a) * 7.2);
      post.material = mat(scene, "#6a4e32");
      add(post);
    }
  }

  // ---- ลานเมืองและเสาหินรอบลาน (เฉพาะเมือง) ----
  if (town) {
    const plaza = MeshBuilder.CreateCylinder("plaza", { diameter: PLAZA_R * 2, height: 0.08, tessellation: 12 }, scene);
    plaza.position.y = 0.04;
    plaza.material = mat(scene, "#b9b3a6");
    add(plaza);

    // วงแหวนหินขอบลานเมือง
    const plazaRim = MeshBuilder.CreateCylinder("plazaRim", { diameter: (PLAZA_R + 0.6) * 2, height: 0.05, tessellation: 12 }, scene);
    plazaRim.position.y = 0.025;
    plazaRim.material = mat(scene, "#8c8477");
    add(plazaRim);
  }

  for (let i = 0; i < (town ? 10 : 0); i++) {
    const a = (i / 10) * Math.PI * 2;
    const st = flat(MeshBuilder.CreateBox("st", { width: 1.6, height: 0.18, depth: 0.9 }, scene));
    st.position.set(Math.cos(a) * (PLAZA_R + 0.4), 0.09, Math.sin(a) * (PLAZA_R + 0.4));
    st.rotation.y = -a;
    st.material = mat(scene, "#8f897d");
    add(st);
  }

  // ---- บ่อน้ำ (เฉพาะเมือง) ----
  const pondAt = { x: -26, z: 22 };
  if (town) {
    const pondEdge = MeshBuilder.CreateDisc("pe", { radius: 6.3, tessellation: 9 }, scene);
    pondEdge.rotation.x = Math.PI / 2;
    pondEdge.position.set(pondAt.x, 0.02, pondAt.z);
    pondEdge.scaling.y = 0.7;
    pondEdge.material = mat(scene, "#d8c08c");
    add(pondEdge);

    const pond = MeshBuilder.CreateDisc("pond", { radius: 5.5, tessellation: 9 }, scene);
    pond.rotation.x = Math.PI / 2;
    pond.position.set(pondAt.x, 0.03, pondAt.z);
    pond.scaling.y = 0.7;
    pond.material = mat(scene, "#4fa9d8", { alpha: 0.92 });
    add(pond);
  }

  const inPond = (x: number, z: number): boolean =>
    town && Math.hypot(x - pondAt.x, (z - pondAt.z) / 0.7) < 7.5;

  // ---- แผงขายของรอบลานเมือง (เฉพาะเมือง) ----
  for (const [x, z, ry, cloth] of (town ? [
    [-4.6, -3.6, 0.7, "#e06b5c"],
    [4.6, -3.6, -0.7, "#5c8de0"],
    [-5, 3.4, 2.4, "#e0b65c"],
  ] : []) as Array<[number, number, number, string]>) {
    buildStall(scene, x, z, ry, cloth);
  }

  // ประตูต้องโล่ง ต้นไม้หรือหินขวางแล้วเดินเข้าไม่ได้ (ฉากไม่มีการชน แต่บังจนมองไม่เห็นทาง)
  const portalAreas = (zone?.portals ?? []).map((p) => ({ x: p.x, z: p.z }));
  const nearPortal = (x: number, z: number): boolean =>
    portalAreas.some((p) => Math.hypot(x - p.x, z - p.z) < PORTAL_RADIUS + 5);
  // โซนที่ไม่ใช่เมือง เว้นที่โล่งรอบจุดเกิดแทนลานเมือง ผู้เล่นโผล่มาจะได้ไม่ติดในพุ่มไม้
  const sp = zone?.spawnPoint ?? { x: 0, z: 0 };
  const nearCenter = (x: number, z: number, r: number): boolean =>
    town ? Math.hypot(x, z) < r : Math.hypot(x - sp.x, z - sp.z) < r;

  /**
   * ห้ามวางของทับจุดเกิดมอนสเตอร์
   *
   * ไม่งั้นมอนจะยืนซ้อนในพุ่มไม้ มองไม่เห็นและคลิกยาก
   * อ่านจาก zones.json ตรง ๆ ย้ายจุดเกิดใน JSON แล้วของตกแต่งหลบตามเอง
   */
  const spawnAreas = (zone?.spawns ?? []).map((s) => ({ x: s.x, z: s.z, r: s.radius + 2 }));
  const inSpawn = (x: number, z: number): boolean =>
    spawnAreas.some((s) => Math.hypot(x - s.x, z - s.z) < s.r);

  const blocked = (x: number, z: number): boolean =>
    nearCenter(x, z, 10) || (town && onPath(x, z)) || inPond(x, z) || inSpawn(x, z) || nearPortal(x, z);

  // แนวทางคดเคี้ยว (โซนนอกเมือง) ต้นไม้หลบช่องทางแล้วขึ้นหนาสองข้าง เหมือนหาทางผ่านป่าเอง
  const routes = routeNetwork(zone);
  const routeD = (x: number, z: number): number => (routes.length ? distToRoute(x, z, routes) : Infinity);

  // ---- ต้นไม้ / สิ่งแวดล้อมเฉพาะโซน หนาแน่นขึ้นที่ขอบแผนที่เพื่อกรอบพื้นที่เล่น ----
  for (let i = 0; i < (town ? 150 : 0); i++) {
    const x = rand() * DECO_REACH * 2 - DECO_REACH;
    const z = rand() * DECO_REACH * 2 - DECO_REACH;
    if (blocked(x, z) || routeD(x, z) < 3.2) continue;
    const edge = Math.max(Math.abs(x), Math.abs(z)) > 33;
    if (!edge && rand() < 0.55) continue;
    buildTree(scene, x, z, 0.8 + rand() * 0.6, rand, add, themeOf(zone));
  }

  // ---- ป่าทึบนอกทางเดิน (โซนนอกเมือง) ----
  // พื้นที่เดินได้มาจาก shared/terrain.ts ตัวเดียวกับที่ server ใช้หนีบการเดิน ป่าที่เห็นจึงคือกำแพงจริง
  // ขอบทางถี่มาก (อ่านออกว่าเป็นกำแพง) ลึกเข้าไปบางลงและต้นใหญ่ขึ้น (หมอกบังอยู่แล้ว ประหยัดจุดยอด)
  if (!town) {
    const thick = makeRandom(zoneSeed(zone) * 13 + 5);
    const STEP = 4;
    const farCol = FAR_TREE[themeOf(zone)] ?? "#3a7a40";
    for (let gx = -H; gx <= H; gx += STEP) {
      for (let gz = -H; gz <= H; gz += STEP) {
        const x = gx + (thick() - 0.5) * 3;
        const z = gz + (thick() - 0.5) * 3;
        const d = wallDistance(zone, x, z);
        if (d < 1.4 || blocked(x, z)) continue;
        const pick = thick();
        if (d < 4.5 && pick < 0.6) {
          // แนวหน้าติดทาง: ต้นไม้เต็มแบบตามธีม (ที่เหลือเป็นกรวย — คุมจุดยอดเมื่อทางมีหลายกิ่ง)
          buildTree(scene, x, z, 0.8 + thick() * 0.6, thick, add, themeOf(zone));
        } else if (pick < (d < 8 ? 0.75 : d < 16 ? 0.35 : 0.1)) {
          // ลึกเข้าไป: กรวยเดียวไม่ flat shade (จุดยอดน้อยมาก หมอกบังรายละเอียดอยู่แล้ว)
          const h = (3 + thick() * 3) * (d < 16 ? 1 : 1.5);
          const cone = MeshBuilder.CreateCylinder("far", { diameterTop: 0, diameterBottom: h * 0.75, height: h, tessellation: 5 }, scene);
          cone.position.set(x, h / 2, z);
          cone.material = mat(scene, farCol);
          add(cone);
        }
      }
    }
  }

  // ---- หินตามธีมของโซน ----
  const rockColors: Record<string, string> = {
    desert: "#c49a6c",
    snow: "#88a2b8",
    dungeon_frost: "#6389a6",
    volcano: "#261a15",
    crystal_cave: "#2d3748",
    ruins: "#7d786e",
    swamp: "#443a2f",
    abyss_ruins: "#1a1426",
    storm: "#5a6070",
    jungle: "#5f6b55",
    graveyard: "#7a7a78",
    celestial: "#e8e4d8",
  };
  const rockCol = rockColors[themeOf(zone)] ?? "#a4a7ad";

  for (let i = 0; i < Math.round(22 * S * S); i++) {
    const x = (rand() * 70 - 35) * S;
    const z = (rand() * 70 - 35) * S;
    if (blocked(x, z) || routeD(x, z) < 2.5) continue;
    const r = flat(MeshBuilder.CreateIcoSphere("rk", { radius: 0.7 * (0.6 + rand() * 0.8), subdivisions: 1 }, scene));
    r.position.set(x, 0.25, z);
    r.scaling.y = 0.6;
    r.rotation.y = rand() * 3;
    r.material = mat(scene, rockCol);
    add(r);
  }

  // ---- ของตกแต่งรายโซนเพิ่มเติม (ตัวสุ่มแยก seed ไม่ขยับผังเดิม) ----
  buildProps({
    scene, zone, town, rand: makeRandom(zoneSeed(zone) * 7 + 101), add, blocked,
    clear: (x, z) => !inSpawn(x, z) && !nearPortal(x, z), routes, half: H,
    wall: (x, z) => (town ? -1 : wallDistance(zone, x, z)),
    mat: (hex, opts) => mat(scene, hex, opts), flat,
  });

  // ---- ดอกไม้/ประกายแสงพื้นตามธีมโซน วางได้ทั่วรวมถึงในลานมอนสเตอร์ เพราะเตี้ยไม่บัง ----
  const flowerThemes: Record<string, string[]> = {
    desert: ["#e9c46a", "#f4a261", "#e76f51"],
    snow: ["#bee3f8", "#e2e8f0", "#90cdf4"],
    dungeon_frost: ["#90cdf4", "#e2e8f0", "#63b3ed"],
    volcano: ["#ff5722", "#ff9800", "#d84315"],
    crystal_cave: ["#4fd1c5", "#9f7aea", "#63b3ed"],
    ruins: ["#a0aec0", "#cbd5e0", "#e2e8f0"],
    swamp: ["#9f7aea", "#b794f4", "#68d391"],
    abyss_ruins: ["#805ad5", "#6b46c1", "#9f7aea"],
    storm: ["#c8d0e0", "#9aa8c0", "#e8e8f0"],
    jungle: ["#ff6a8a", "#ffb03a", "#c45ab0"],
    graveyard: ["#c8c8d0", "#8a7aa8", "#e0e0e0"],
    celestial: ["#ffe08a", "#ffffff", "#fff0c0"],
  };
  const petals = flowerThemes[themeOf(zone)] ?? ["#ffffff", "#ffe36b", "#ff9fc4"];

  // หญ้า ดอกไม้ ก้อนเล็ก หลายพันชิ้น (thin instances · ground-detail.ts) — ตัวสุ่มแยก ผังต้นไม้เดิมไม่ขยับ
  scatterGround(scene, themeOf(zone), zone?.ground ?? DEFAULT_GROUND, H, zoneSeed(zone),
    (x, z) => nearCenter(x, z, 8) || (town && onPath(x, z)) || inPond(x, z), petals, quality);

  mergeDecoration(deco);
}

/** แผงขายของ ไม่ได้รวมเป็นก้อนเดียวเพราะหมุนอยู่ มีแค่สามหลังจึงไม่คุ้มที่จะยุ่ง */
function buildStall(scene: Scene, x: number, z: number, ry: number, cloth: string): void {
  const n = new TransformNode("stall", scene);
  n.position.set(x, 0, z);
  n.rotation.y = ry;

  const table = flat(MeshBuilder.CreateBox("t", { width: 2.4, height: 0.9, depth: 1.2 }, scene));
  table.position.y = 0.45;
  table.material = mat(scene, "#9a7348");
  table.parent = n;

  const roof = flat(MeshBuilder.CreateCylinder(
    "r",
    { diameterTop: 0, diameterBottom: 3.4, height: 1, tessellation: 4 },
    scene,
  ));
  roof.position.y = 2.3;
  roof.rotation.y = Math.PI / 4;
  roof.material = mat(scene, cloth);
  roof.parent = n;

  for (const [a, b] of [[-1, -0.5], [1, -0.5], [-1, 0.5], [1, 0.5]] as Array<[number, number]>) {
    const p = MeshBuilder.CreateCylinder("pp", { diameter: 0.12, height: 1.9, tessellation: 5 }, scene);
    p.position.set(a, 0.95, b);
    p.material = mat(scene, "#5a4632");
    p.parent = n;
  }

  for (const m of n.getChildMeshes()) m.isPickable = false;
}

function buildTree(
  scene: Scene,
  x: number,
  z: number,
  s: number,
  rand: () => number,
  add: (m: Mesh) => void,
  zoneId?: string,
): void {
  if (zoneId === "desert") {
    // กระบองเพชร Saguaro ในทะเลทราย
    const cactus = flat(MeshBuilder.CreateCylinder("cact", { diameter: 0.5 * s, height: 3.0 * s, tessellation: 6 }, scene));
    cactus.position.set(x, 1.5 * s, z);
    cactus.material = mat(scene, "#386641");
    add(cactus);

    for (const d of [-1, 1]) {
      if (rand() > 0.3) {
        const armH = flat(MeshBuilder.CreateBox("cactArmH", { width: 0.65 * s, height: 0.3 * s, depth: 0.3 * s }, scene));
        armH.position.set(x + d * 0.45 * s, (1.3 + d * 0.25) * s, z);
        armH.material = mat(scene, "#386641");
        add(armH);

        const armV = flat(MeshBuilder.CreateCylinder("cactArmV", { diameter: 0.32 * s, height: 0.95 * s, tessellation: 5 }, scene));
        armV.position.set(x + d * 0.75 * s, (1.7 + d * 0.25) * s, z);
        armV.material = mat(scene, "#386641");
        add(armV);
      }
    }
    return;
  }

  if (zoneId === "snow" || zoneId === "dungeon_frost") {
    // ต้นสนเคลือบหิมะ
    const trunk = flat(MeshBuilder.CreateCylinder("tr", { diameter: 0.45 * s, height: 1.4 * s, tessellation: 5 }, scene));
    trunk.position.set(x, 0.7 * s, z);
    trunk.material = mat(scene, "#4a3525");
    add(trunk);

    const c1 = flat(MeshBuilder.CreateCylinder("c1", { diameterTop: 0, diameterBottom: 2.6 * s, height: 2.2 * s, tessellation: 6 }, scene));
    c1.position.set(x, 2.2 * s, z);
    c1.material = mat(scene, "#2d4a3e");
    add(c1);

    const snowCap = flat(MeshBuilder.CreateCylinder("sc", { diameterTop: 0, diameterBottom: 2.0 * s, height: 1.2 * s, tessellation: 6 }, scene));
    snowCap.position.set(x, 2.9 * s, z);
    snowCap.material = mat(scene, "#edf2f7");
    add(snowCap);

    const c2 = flat(MeshBuilder.CreateCylinder("c2", { diameterTop: 0, diameterBottom: 1.8 * s, height: 1.8 * s, tessellation: 6 }, scene));
    c2.position.set(x, 3.4 * s, z);
    c2.material = mat(scene, "#edf2f7");
    add(c2);
    return;
  }

  if (zoneId === "volcano") {
    // เสาหินอัคนีภูเขาไฟ
    const spire = flat(MeshBuilder.CreateCylinder("spire", { diameterTop: 0.1 * s, diameterBottom: 1.4 * s, height: 3.5 * s, tessellation: 5 }, scene));
    spire.position.set(x, 1.75 * s, z);
    spire.rotation.x = (rand() - 0.5) * 0.3;
    spire.rotation.z = (rand() - 0.5) * 0.3;
    spire.material = mat(scene, rand() > 0.5 ? "#211815" : "#1a120f");
    add(spire);

    if (rand() > 0.4) {
      const ember = flat(MeshBuilder.CreateSphere("ember", { diameter: 0.4 * s, segments: 3 }, scene));
      ember.position.set(x, 0.5 * s, z);
      ember.material = mat(scene, "#ff5722", { em: "#ff7043", noLight: true });
      add(ember);
    }
    return;
  }

  if (zoneId === "crystal_cave") {
    // เสาผลึกคริสตัลเรืองแสง
    const colors = ["#4fd1c5", "#b794f4", "#63b3ed", "#f687b3"];
    const col = colors[Math.floor(rand() * colors.length)]!;
    const crys = flat(MeshBuilder.CreateCylinder("crys", { diameterTop: 0, diameterBottom: 0.9 * s, height: 3.2 * s, tessellation: 6 }, scene));
    crys.position.set(x, 1.6 * s, z);
    crys.rotation.x = (rand() - 0.5) * 0.4;
    crys.rotation.z = (rand() - 0.5) * 0.4;
    crys.material = mat(scene, col, { em: col, noLight: true });
    add(crys);
    return;
  }

  if (zoneId === "root_cave" || zoneId === "dungeon_mine") {
    // หินงอกกับเห็ดเรืองแสง
    const h = (2 + rand() * 1.6) * s;
    const mite = flat(MeshBuilder.CreateCylinder("mite", { diameterTop: 0, diameterBottom: 1.1 * s, height: h, tessellation: 5 }, scene));
    mite.position.set(x, h / 2, z);
    mite.material = mat(scene, zoneId === "root_cave" ? "#4a3b2c" : "#3e342b");
    add(mite);
    if (rand() > 0.35) {
      const glow = zoneId === "root_cave" ? "#7dff9a" : "#ffc76b";
      const stem = MeshBuilder.CreateCylinder("mstem", { diameter: 0.14 * s, height: 0.4 * s, tessellation: 5 }, scene);
      stem.position.set(x + 0.8 * s, 0.2 * s, z + 0.3 * s);
      stem.material = mat(scene, "#e8dcc4");
      add(stem);
      const capM = MeshBuilder.CreateSphere("mcap", { diameter: 0.5 * s, segments: 3, slice: 0.5 }, scene);
      capM.position.set(x + 0.8 * s, 0.38 * s, z + 0.3 * s);
      capM.material = mat(scene, glow, { em: glow, noLight: true });
      add(capM);
    }
    return;
  }

  if (zoneId === "ruins") {
    // เสาหินวิหารโบราณ
    const h = (1.5 + rand() * 1.8) * s;
    const pillar = flat(MeshBuilder.CreateCylinder("pillar", { diameter: 0.9 * s, height: h, tessellation: 8 }, scene));
    pillar.position.set(x, h * 0.5, z);
    pillar.material = mat(scene, "#9a9686");
    add(pillar);

    if (rand() > 0.4) {
      const cap = flat(MeshBuilder.CreateBox("pillarCap", { width: 1.3 * s, height: 0.35 * s, depth: 1.3 * s }, scene));
      cap.position.set(x, h + 0.17 * s, z);
      cap.material = mat(scene, "#7d786e");
      add(cap);
    }
    return;
  }

  if (zoneId === "abyss_ruins") {
    // เสาศิลาแห่งความมืด
    const obelisk = flat(MeshBuilder.CreateCylinder("obelisk", { diameterTop: 0.2 * s, diameterBottom: 1.1 * s, height: 4.0 * s, tessellation: 4 }, scene));
    obelisk.position.set(x, 2.0 * s, z);
    obelisk.rotation.y = Math.PI / 4;
    obelisk.material = mat(scene, "#1a102f");
    add(obelisk);

    const rune = flat(MeshBuilder.CreateSphere("voidRune", { diameter: 0.45 * s, segments: 3 }, scene));
    rune.position.set(x, 2.8 * s, z);
    rune.material = mat(scene, "#9f7aea", { em: "#805ad5", noLight: true });
    add(rune);
    return;
  }

  if (zoneId === "swamp") {
    // ต้นไม้หนองน้ำ
    const trunk = flat(MeshBuilder.CreateCylinder("swampTrunk", { diameterTop: 0.6 * s, diameterBottom: 0.9 * s, height: 1.8 * s, tessellation: 5 }, scene));
    trunk.position.set(x, 0.9 * s, z);
    trunk.material = mat(scene, "#382d24");
    add(trunk);

    const canopy = flat(MeshBuilder.CreateSphere("swampCanopy", { diameter: 2.8 * s, segments: 2 }, scene));
    canopy.scaling.set(1.2, 0.6, 1.2);
    canopy.position.set(x, 2.4 * s, z);
    canopy.material = mat(scene, rand() > 0.5 ? "#4a5338" : "#5d4a66");
    add(canopy);
    return;
  }

  if (zoneId === "coast") {
    // ต้นมะพร้าวริมหาด
    const palmTrunk = flat(MeshBuilder.CreateCylinder("palmTrunk", { diameterTop: 0.3 * s, diameterBottom: 0.55 * s, height: 2.8 * s, tessellation: 5 }, scene));
    palmTrunk.position.set(x, 1.4 * s, z);
    palmTrunk.rotation.z = (rand() - 0.5) * 0.3;
    palmTrunk.material = mat(scene, "#8c6747");
    add(palmTrunk);

    const fronds = flat(MeshBuilder.CreateSphere("palmFronds", { diameter: 2.6 * s, segments: 2 }, scene));
    fronds.scaling.set(1.2, 0.25, 1.2);
    fronds.position.set(x, 2.8 * s, z);
    fronds.material = mat(scene, "#2f855a");
    add(fronds);
    return;
  }

  if (zoneId === "jungle") {
    // ต้นไม้ใหญ่เรือนยอดกว้าง มีเถาวัลย์ห้อย
    const trunk = flat(MeshBuilder.CreateCylinder("jtr", { diameterTop: 0.5 * s, diameterBottom: 0.9 * s, height: 3.2 * s, tessellation: 6 }, scene));
    trunk.position.set(x, 1.6 * s, z);
    trunk.material = mat(scene, "#5a4030");
    add(trunk);
    const top = flat(MeshBuilder.CreateSphere("jtop", { diameter: 3.6 * s, segments: 2 }, scene));
    top.scaling.y = 0.55;
    top.position.set(x, 3.6 * s, z);
    top.material = mat(scene, rand() > 0.5 ? "#2f7a3a" : "#3f8f4a");
    add(top);
    for (const d of [-1, 1]) {
      const vine = MeshBuilder.CreateCylinder("vine", { diameter: 0.08, height: 1.6 * s, tessellation: 4 }, scene);
      vine.position.set(x + d * 1.1 * s, 2.6 * s, z + 0.4 * d);
      vine.material = mat(scene, "#4f8a2a");
      add(vine);
    }
    return;
  }

  if (zoneId === "storm") {
    // สนเอนลู่ลม
    const lean = (rand() - 0.5) * 0.5;
    const trunk = flat(MeshBuilder.CreateCylinder("str", { diameter: 0.35 * s, height: 1.8 * s, tessellation: 5 }, scene));
    trunk.position.set(x, 0.9 * s, z);
    trunk.rotation.z = lean;
    trunk.material = mat(scene, "#3a3430");
    add(trunk);
    const c1 = flat(MeshBuilder.CreateCylinder("sc1", { diameterTop: 0, diameterBottom: 1.8 * s, height: 2.4 * s, tessellation: 5 }, scene));
    c1.position.set(x - lean * 1.6 * s, 2.6 * s, z);
    c1.rotation.z = lean;
    c1.material = mat(scene, "#2f4a40");
    add(c1);
    return;
  }

  if (zoneId === "graveyard") {
    // ต้นไม้ตายกิ่งบิด
    const trunk = flat(MeshBuilder.CreateCylinder("gtr", { diameterTop: 0.15, diameterBottom: 0.6 * s, height: 2.8 * s, tessellation: 5 }, scene));
    trunk.position.set(x, 1.4 * s, z);
    trunk.rotation.z = (rand() - 0.5) * 0.25;
    trunk.material = mat(scene, "#2e2a28");
    add(trunk);
    for (const d of [-1, 1]) {
      const br = flat(MeshBuilder.CreateCylinder("gbr", { diameterTop: 0.04, diameterBottom: 0.14, height: 1.2 * s, tessellation: 4 }, scene));
      br.position.set(x + d * 0.45 * s, (1.9 + d * 0.3) * s, z);
      br.rotation.z = -d * 0.9;
      br.material = mat(scene, "#2e2a28");
      add(br);
    }
    return;
  }

  if (zoneId === "celestial") {
    // ต้นไม้ใบทองเปลือกขาว
    const trunk = flat(MeshBuilder.CreateCylinder("ctr", { diameter: 0.4 * s, height: 2 * s, tessellation: 6 }, scene));
    trunk.position.set(x, 1 * s, z);
    trunk.material = mat(scene, "#f0ece0");
    add(trunk);
    const top = flat(MeshBuilder.CreateIcoSphere("ctop", { radius: 1.3 * s, subdivisions: 1 }, scene));
    top.position.set(x, 2.7 * s, z);
    top.material = mat(scene, rand() > 0.5 ? "#f2c94c" : "#ffe08a", { em: "#8a6a10" });
    add(top);
    return;
  }

  // ป่า และ ทุ่งหญ้า (ทุ่งดอกเช้า, เนินลมพัด, ป่าเงาไม้)
  const trunk = flat(MeshBuilder.CreateCylinder("tr", { diameter: 0.45 * s, height: 1.4 * s, tessellation: 5 }, scene));
  trunk.position.set(x, 0.7 * s, z);
  trunk.material = mat(scene, "#6b4a2e");
  add(trunk);

  const shade = zoneId === "forest" ? (rand() > 0.5 ? "#2d5a27" : "#1e4620") : (rand() > 0.5 ? "#3f8f4a" : "#4ea152");
  const c1 = flat(MeshBuilder.CreateCylinder(
    "c1",
    { diameterTop: 0, diameterBottom: 2.6 * s, height: 2.2 * s, tessellation: 6 },
    scene,
  ));
  c1.position.set(x, 2.2 * s, z);
  c1.material = mat(scene, shade);
  add(c1);

  const c2 = flat(MeshBuilder.CreateCylinder(
    "c2",
    { diameterTop: 0, diameterBottom: 1.9 * s, height: 1.8 * s, tessellation: 6 },
    scene,
  ));
  c2.position.set(x, 3.3 * s, z);
  c2.rotation.y = 0.5;
  c2.material = mat(scene, shade);
  add(c2);
}

function applyAtmosphere(scene: Scene, zoneId?: string, groundHex?: string): void {
  const configs: Record<string, { sky: string; fog: string; density: number }> = {
    meadow: { sky: "#a0d8ef", fog: "#a0d8ef", density: 0.003 },
    hills: { sky: "#98d2eb", fog: "#98d2eb", density: 0.004 },
    forest: { sky: "#9cc4a4", fog: "#7fa486", density: 0.008 },
    coast: { sky: "#7ec8e3", fog: "#9cd6ec", density: 0.004 },
    root_cave: { sky: "#2d2319", fog: "#3d2e20", density: 0.015 },
    desert: { sky: "#f2dda4", fog: "#eed28c", density: 0.007 },
    dungeon_mine: { sky: "#241e17", fog: "#332a20", density: 0.016 },
    ruins: { sky: "#7a8288", fog: "#8c949b", density: 0.009 },
    swamp: { sky: "#3d3047", fog: "#4a3b56", density: 0.014 },
    crystal_cave: { sky: "#161b2b", fog: "#1f263d", density: 0.015 },
    snow: { sky: "#bad7ea", fog: "#cde3f2", density: 0.008 },
    dungeon_frost: { sky: "#1b2c3a", fog: "#243c4f", density: 0.014 },
    volcano: { sky: "#2d120d", fog: "#3d1912", density: 0.012 },
    abyss_ruins: { sky: "#0e0818", fog: "#180e29", density: 0.016 },
    storm: { sky: "#4a5260", fog: "#5a6270", density: 0.011 },
    jungle: { sky: "#93bd8c", fog: "#6c9466", density: 0.012 },
    graveyard: { sky: "#5a5f66", fog: "#6a7078", density: 0.016 },
    celestial: { sky: "#cfe4ff", fog: "#eaf2ff", density: 0.004 },
    misty_bamboo: { sky: "#88b594", fog: "#9ec9a8", density: 0.011 },
    mangrove_swamp: { sky: "#628b7e", fog: "#76a495", density: 0.010 },
    ant_nest: { sky: "#231812", fog: "#342217", density: 0.016 },
    rainbow_oasis: { sky: "#a8e0f5", fog: "#c5effd", density: 0.005 },
  };

  const cfg = configs[zoneId ?? "meadow"] ?? configs["meadow"]!;
  const skyCol = Color3.FromHexString(cfg.sky);
  const fogCol = Color3.FromHexString(cfg.fog);
  scene.clearColor = new Color4(fogCol.r, fogCol.g, fogCol.b, 1);
  scene.fogMode = Scene.FOGMODE_LINEAR;
  scene.fogColor = fogCol;
  // มองเห็นแค่รอบตัว (ผู้ใช้สั่ง 9 ต.ค.) แต่หมอกนับจาก "ตัวละคร" ไม่ใช่จากกล้อง (ผู้ใช้ติ 10 ต.ค.: ฉากขุ่นทั้งจอ ไม่สวย)
  // เดิมหมอกเริ่มที่ 32 จากกล้อง = แทบตรงตัวละครพอดี (กล้องห่าง 22–30) ทั้งจอจึงจางเท่ากันหมด
  // ตอนนี้: รอบตัวใส · เริ่มจางห่างตัว ~16 · มิดห่างตัว ~42 (ธีมหมอกหนาใกล้ขึ้น) — คิดใหม่ทุกเฟรมตามระยะซูม (`followFog`)
  const thick = Math.min(1, cfg.density / 0.02);
  // กล้องมุมสูง (รอบ 20) เห็นแค่รอบตัวอยู่แล้ว หมอกจึงเป็นแค่บรรยากาศไกล ๆ ไม่ต้องหนา
  fogNear = 24 - 8 * thick;
  fogFar = 58 - 14 * thick;
  paintSky(scene, skyCol, fogCol);
  lightFor(scene, skyCol, fogCol, groundHex ? Color3.FromHexString(groundHex) : undefined);
}

/** ระยะหมอกนับจากตัวละคร (ตั้งใน applyAtmosphere ตามธีม) */
let fogNear = 16;
let fogFar = 42;

/** ตามระยะซูมของกล้อง: ตัวละครอยู่ห่างกล้อง = radius หมอกจึงเริ่มหลังตัวละครเสมอไม่ว่าจะซูมเท่าไร */
function followFog(scene: Scene): void {
  const cam = scene.activeCamera as { radius?: number } | null;
  const r = typeof cam?.radius === "number" ? cam.radius : 22;
  scene.fogStart = r + fogNear;
  scene.fogEnd = r + fogFar;
}

/**
 * ท้องฟ้าไล่สี: ขอบฟ้า = สีหมอก (ของไกลจมหายไปกับฟ้าเนียน ๆ) · กลางฟ้า = สีฟ้าของธีมเข้มขึ้น
 * โดมเดียวต่อฉาก `infiniteDistance` ตามกล้องเอง ไม่โดนหมอก ไม่รับคลิก — สลับโซนแค่ทาสีจุดยอดใหม่
 */
const SKY_NAME = "sky-dome";
function paintSky(scene: Scene, sky: Color3, horizon: Color3): void {
  let dome = scene.getMeshByName(SKY_NAME) as Mesh | null;
  if (!dome) {
    dome = MeshBuilder.CreateSphere(SKY_NAME, { diameter: 900, segments: 16, sideOrientation: Mesh.BACKSIDE }, scene);
    dome.infiniteDistance = true;
    dome.isPickable = false;
    dome.applyFog = false;
    const m = new StandardMaterial("sky-mat", scene);
    m.disableLighting = true;
    m.emissiveColor = new Color3(1, 1, 1);
    m.diffuseColor = new Color3(0, 0, 0);
    m.specularColor = new Color3(0, 0, 0);
    m.backFaceCulling = false;
    m.fogEnabled = false;
    dome.material = m;
    dome.renderingGroupId = 0;
  }
  const pos = dome.getVerticesData(VertexBuffer.PositionKind) ?? [];
  const zenith = Color3.Lerp(sky, new Color3(sky.r * 0.55, sky.g * 0.7, Math.min(1, sky.b * 1.05)), 0.6);
  const cols: number[] = [];
  for (let i = 0; i < pos.length; i += 3) {
    const y = pos[i + 1]! / 450;
    // ใต้ขอบฟ้า = สีหมอก · เหนือขึ้นไปไล่เป็นสีฟ้าแล้วเข้มขึ้นที่กลางฟ้า
    const t = Math.max(0, Math.min(1, y * 2.2));
    const c = t < 0.5 ? Color3.Lerp(horizon, sky, t * 2) : Color3.Lerp(sky, zenith, (t - 0.5) * 2);
    cols.push(c.r, c.g, c.b, 1);
  }
  dome.setVerticesData(VertexBuffer.ColorKind, cols);
}

/**
 * แสงตามธีม: โซนกลางวันได้แดดอุ่นและเงาตัดกันชัด · ถ้ำ/โซนมืดได้แสงเย็นสลัว
 * ไฟ hemi/sun สร้างใน main.ts (ชื่อ "hemi" "sun") — ไม่มี (เทสต์/หน้าอื่น) ก็ข้าม
 */
function lightFor(scene: Scene, sky: Color3, fog: Color3, ground?: Color3): void {
  const lumOf = (c: Color3): number => 0.3 * c.r + 0.59 * c.g + 0.11 * c.b;
  const day = Math.max(0, Math.min(1, (lumOf(sky) - 0.15) / 0.6));
  // พื้นสีอ่อนมาก (หิมะ) ลดแสงลง ไม่งั้นขาวโพลนจนไม่เห็นทรงของ
  const pale = ground ? Math.max(0, lumOf(ground) - 0.72) * 0.5 : 0;
  const k = 1 - pale;
  // สีแสงหลักของโซนมืดเอียงตามสีหมอก (ภูเขาไฟแดงส้ม ถ้ำฟ้า หนองม่วง) · โซนกลางวัน = แดดอุ่น
  const m = Math.max(fog.r, fog.g, fog.b, 0.01);
  const hue = new Color3(fog.r / m, fog.g / m, fog.b / m);
  const hemi = scene.getLightByName("hemi") as (HemisphericLight | null);
  const sun = scene.getLightByName("sun") as (DirectionalLight | null);
  if (hemi) {
    // โซนมืดได้แสงรอบทิศ (hemi) เพิ่ม ให้ยังเห็นทรงของ ไม่ดำเป็นก้อน
    hemi.intensity = (0.72 - 0.12 * day) * k;
    hemi.diffuse = Color3.Lerp(new Color3(1, 1, 1), hue, 0.25 + 0.2 * (1 - day));
    hemi.groundColor = Color3.Lerp(new Color3(0.3, 0.32, 0.38), hue.scale(0.5), 0.4);
    hemi.specular = new Color3(0, 0, 0);
  }
  if (sun) {
    sun.intensity = (0.5 + 0.25 * day) * k;
    sun.diffuse = Color3.Lerp(Color3.Lerp(new Color3(1, 1, 1), hue, 0.55), new Color3(1, 0.93, 0.78), day);
    sun.specular = new Color3(0.15, 0.15, 0.15);
  }
}

/**
 * รวมของตกแต่งทั้งหมดเป็น mesh ก้อนเดียว
 *
 * ของตกแต่งมีหลายร้อยชิ้นแต่ไม่ขยับเลย ถ้าปล่อยแยกกันจะเสีย draw call ชิ้นละครั้ง
 * รวมแล้ว freeze world matrix ทิ้งไว้ Babylon จะไม่ต้องคิดเมทริกซ์ใหม่ทุกเฟรม
 * `multiMultiMaterials` = true เพื่อให้ของต่างสีรวมกันได้โดยไม่เสียสี
 */
function mergeDecoration(deco: Mesh[]): void {
  if (deco.length === 0) return;
  try {
    const merged = Mesh.MergeMeshes(deco, true, true, undefined, false, true);
    if (merged) {
      // ตั้งชื่อให้แน่นอน เทสต์กับการไล่ปัญหาจะได้หาเจอ
      merged.name = DECO_MESH_NAME;
      merged.isPickable = false;
      merged.freezeWorldMatrix();
    }
  } catch {
    // รวมไม่สำเร็จก็ยังเล่นได้ แค่เปลืองกว่าเดิม ดีกว่าฉากหายทั้งฉาก
    for (const m of deco) m.isPickable = false;
  }
}
